import React, { ChangeEvent, Dispatch, FormEvent, useCallback, useEffect, useReducer } from 'react';

import { Button, Container, Divider, FormControl, Grid, makeStyles, Typography } from '@material-ui/core';

import { Icon, TextField, Translate, NotificationBanner } from '@hydra/common/components';
import SessionTable from './SessionTable';

import * as fetcher from '@hydra/utils/fetcher';
import { changePassword, destroySessions } from '@hydra/utils/accountUtil';
import { handleInvalidPasswordLimit, isAttemptAllowed } from '@hydra/utils/invalidPasswordAttempt';

import { useI18n } from '@hydra/common/customHooks';

interface Field {
  id: string,
  label: string,
  required?: boolean,
  minLength?: number,
  touched: boolean,
  valid: boolean,
  errorMessage?: string,
  value: string
}

interface State {
  fields: {
    [key: string]: Field
  },
  valid: boolean,
  alert?: {
    severity: 'error' | 'info' | 'success' | 'warning',
    message: string
  },
  loading: boolean
}

const useStyles = makeStyles(theme => ({
  heading: {
    padding: theme.spacing(4, 0)
  },
  content: {
    padding: theme.spacing(4, 0)
  },
  alert: {
    marginBottom: theme.spacing(2)
  },
  subHeading: {
    marginBottom: theme.spacing(4)
  },
  formRow: {
    marginBottom: theme.spacing(2)
  },
  buttons: {
    marginTop: theme.spacing(2)
  },
  submitButtonText: {
    position: 'relative'
  },
  loadingIndicator: {
    position: 'absolute',
    left: theme.spacing(-3),
    top: theme.spacing(0.75)
  },
  description: {
    padding: theme.spacing(0, 0, 4, 0)
  },
  textWrapper: {
    width: '100%'
  }
}));

const LOCALSTORAGE_KEY = '_wr_security_invalid_password_limit_data';

const Security = () => {
  const { locale, translate } = useI18n();
  const classes = useStyles();

  const validateField = (state: State, fieldName: string, value: any): { valid: boolean, errorMessage?: string } => {
    const field: Field = state.fields[fieldName];

    if (field.required && !value) {
      return {
        valid: false,
        errorMessage: translate('validator_required', {
          with: {
            field: field.label
          }
        })
      };
    } else if (field.minLength && value && value.length < field.minLength) {
      return {
        valid: false,
        errorMessage: translate('validator_min_length', {
          with: {
            field: field.label,
            minLength: field.minLength
          }
        })
      };
    }

    return {
      valid: true
    };
  };

  const validateForm = useCallback((state: State): void => {
    let valid = Object.getOwnPropertyNames(state.fields).every(f => {
      const field = state.fields[f];

      return field.valid;
    });

    const newPassword = state.fields.newPassword;
    const newPasswordVerification = state.fields.newPasswordVerification;
    const newPasswordVerified = !!newPasswordVerification.value && newPasswordVerification.value === newPassword.value;

    if (newPasswordVerified !== newPasswordVerification.valid) {
      setFieldValidity('newPasswordVerification', newPasswordVerified, translate('validator_password_verification'));
    }

    if (!newPasswordVerified) {
      valid = false;
    }

    if (state.valid !== valid) {
      dispatch({ type: 'SET_FORM_VALIDITY', valid: valid });
    }
  }, [translate]);

  const reducer = (previousState: State, action: any): State => {
    switch (action.type) {
      case 'RESET_FORM':
      {
        const newState = {
          ...previousState,
          valid: false
        };

        Object.keys(newState.fields).forEach(fieldName => {
          const field = newState.fields[fieldName];

          field.touched = false;
          field.valid = false;
          field.errorMessage = undefined;
          field.value = '';
        });

        return newState;
      }
      case 'SET_ALERT':
        return {
          ...previousState,
          alert: action.severity && action.message
            ? {
                severity: action.severity,
                message: action.message
              }
            : undefined
        };
      case 'SET_FIELD_VALIDITY':
      {
        const field: Field = previousState.fields[action.field];

        return {
          ...previousState,
          fields: {
            ...previousState.fields,
            [action.field]: {
              ...field,
              valid: action.valid,
              errorMessage: action.errorMessage
            }
          }
        };
      }
      case 'SET_FIELD_VALUE':
      {
        const field: Field = previousState.fields[action.field];
        const validation = validateField(previousState, action.field, action.value);

        return {
          ...previousState,
          fields: {
            ...previousState.fields,
            [action.field]: {
              ...field,
              touched: true,
              valid: validation.valid,
              errorMessage: validation.errorMessage,
              value: action.value
            }
          }
        };
      }
      case 'SET_FORM_ERRORS':
      {
        const newState = {
          ...previousState,
          valid: false
        };

        const fieldErrors = action.fieldErrors;

        if (fieldErrors) {
          Object.keys(fieldErrors).forEach(fieldName => {
            const field = newState.fields[fieldName];
            const error = fieldErrors[fieldName];

            if (field && error) {
              field.valid = false;
              field.errorMessage = translate(error.message, {
                with: {
                  field: fieldName,
                  ...error
                }
              });
            }
          });
        }

        return newState;
      }
      case 'SET_FORM_VALIDITY':
        return {
          ...previousState,
          valid: action.valid
        };
      case 'SET_LOADING':
        return {
          ...previousState,
          loading: action.loading
        };
      default:
        throw new Error(`Unsupported action type: ${action.type}`);
    }
  };

  const [state, dispatch]: [state: State, dispatch: Dispatch<any>] = useReducer(reducer, {
    fields: {
      oldPassword: {
        id: 'old-password',
        label: translate('account_security_old_password'),
        required: true,
        touched: false,
        valid: false,
        value: ''
      },
      newPassword: {
        id: 'new-password',
        label: translate('account_security_new_password'),
        required: true,
        minLength: 6,
        touched: false,
        valid: false,
        value: ''
      },
      newPasswordVerification: {
        id: 'new-password-verification',
        label: translate('account_security_new_password_verification'),
        required: true,
        touched: false,
        valid: false,
        value: ''
      }
    },
    valid: false,
    loading: false
  });

  useEffect(() => {
    validateForm(state);
  }, [validateForm, state]);

  const handleFieldChange = (event: ChangeEvent<any>, fieldName: string) => {
    dispatch({ type: 'SET_FIELD_VALUE', field: fieldName, value: event.target.value });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isAttemptAllowed(LOCALSTORAGE_KEY)) {
      setAlert('error', translate('account_security_max_invalid_attempts'));
      return;
    }
    if (!state.valid) {
      return;
    }

    try {
      setAlert();
      setLoading(true);

      await changePassword(state.fields.oldPassword.value, state.fields.newPassword.value);
      await destroySessions(locale);

      setAlert('success', translate('account_security_success_message'));
      resetForm();
    } catch (error: any) {
      let details: any;

      if (error instanceof fetcher.HTTPError) {
        details = await error.response.json();
      }

      if (details?.fields && Object.keys(details.fields).length) {
        handleInvalidPasswordLimit(LOCALSTORAGE_KEY, details.fields);
        setFormErrors(details.fields);
      } else if (details?.limitError) {
        setAlert('error', translate(details.limitError));
      } else {
        setAlert('error', translate('generic_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const setAlert = (severity?: 'error' | 'info' | 'success' | 'warning', message?: string) => {
    dispatch({ type: 'SET_ALERT', severity: severity, message: message });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', loading: loading });
  };

  const setFieldValidity = (fieldName: string, valid: boolean, errorMessage?: string) => {
    dispatch({ type: 'SET_FIELD_VALIDITY', field: fieldName, valid: valid, errorMessage: errorMessage });
  };

  const setFormErrors = (fieldErrors: { [fieldName: string]: string }) => {
    dispatch({ type: 'SET_FORM_ERRORS', fieldErrors: fieldErrors });
  };

  const resetForm = () => {
    dispatch({ type: 'RESET_FORM' });
  };

  const textInput = (fieldName: string, type: string = 'text') => {
    const field: Field = state.fields[fieldName];

    if (!field) {
      return null;
    }

    return (
      <FormControl className={classes.formRow}>
        <TextField
          label={field.label}
          id={`change-password-${field.id}`}
          type={type}
          value={field.value}
          required={field.required ?? false}
          error={field.touched && !field.valid}
          helperText={field.touched && !field.valid && field.errorMessage ? field.errorMessage : null}
          onChange={event => handleFieldChange(event, fieldName)}
        />
      </FormControl>
    );
  };

  return (
    <>
      <Container maxWidth='lg' className='nr-account-security'>
        <Typography component='h1' variant='h2' className={classes.heading}>
          <Translate content='account_security_title' />
        </Typography>
      </Container>
      <Divider />
      <Container maxWidth='lg'>
        <div className={classes.content}>
          <Grid container>
            <Grid item sm={12} md={6}>
              <Typography component='h2' variant='h3' className={classes.subHeading}>
                <Translate content='change_password' />
              </Typography>
              {state.alert?.message && (
                <div className={classes.alert}>
                  <NotificationBanner
                    severity={state.alert.severity}
                    title={state.alert.message}
                    dismissable
                  />
                </div>
              )}
              <FormControl
                component='form'
                fullWidth
                onSubmit={handleSubmit}
              >
                {textInput('oldPassword', 'password')}
                {textInput('newPassword', 'password')}
                {textInput('newPasswordVerification', 'password')}
                <div className={classes.buttons}>
                  <Button
                    variant='contained'
                    color='primary'
                    size='large'
                    type='submit'
                    fullWidth
                    disabled={!state.valid || state.loading}
                  >
                    <div className={classes.submitButtonText}>
                      {state.loading && <Icon type='loader' size='sm' className={classes.loadingIndicator} />}
                      <Translate content='save' />
                    </div>
                  </Button>
                </div>
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </Container>
      <Divider />
      <Container maxWidth='lg'>
        <div className={classes.content}>
          <Grid container>
            <Grid item sm={12} md={12} className={classes.textWrapper}>
              <Typography component='h2' variant='h3' className={classes.subHeading}>
                <Translate content='account_security_active_sessions' />
              </Typography>
              <Grid container className={classes.description}>
                <Grid item md={7} sm={12}>
                  <Typography>
                    <Translate content='account_security_sessions_table_info' />
                  </Typography>
                </Grid>
              </Grid>
              <SessionTable />
            </Grid>
          </Grid>
        </div>
      </Container>
    </>
  );
};

export default Security;
